import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandList,
  Dialog,
  DialogContent,
  DialogTrigger,
  Field,
  RHF,
  cn,
} from "@devhop/ui";
import { useVirtualizer } from "@tanstack/react-virtual";
import * as LucideIcons from "lucide-react";
import { Check, ChevronDown } from "lucide-react";
import { useMemo, useRef, useState } from "react";

const NON_ICON_EXPORTS = new Set([
  "createLucideIcon",
  "Icon",
  "LucideProps",
  "default",
  "ForwardRefExoticComponent",
  "RefAttributes",
]);

// Icon picker: ใช้ keys จาก lucide-react เพื่อแสดงรายการ icon (trade-off: bundle มีทุก icon)
const iconNames = (
  Object.keys(LucideIcons) as (keyof typeof LucideIcons)[]
).filter(
  (key) =>
    // biome-ignore lint/performance/noDynamicNamespaceImportAccess: ต้อง iterate keys เพื่อสร้างรายการ icon
    /^[A-Z]/.test(key) && !NON_ICON_EXPORTS.has(key),
) as string[];

function DynamicIcon({
  name,
  className,
}: {
  name: string;
  className?: string;
}) {
  // biome-ignore lint/performance/noDynamicNamespaceImportAccess: icon picker ต้อง resolve by name
  const IconComponent = LucideIcons[name as keyof typeof LucideIcons] as
    | React.ComponentType<{ className?: string; size?: number }>
    | undefined;

  if (!IconComponent) {
    return null;
  }

  return <IconComponent className={className} size={16} />;
}

type FormIconSelectProps = {
  name: string;
  label?: React.ReactNode;
  hint?: React.ReactNode;
  requiredMark?: boolean;
  placeholder?: string;
  className?: string;
  /**
   * Optional array of icon names to filter. If provided, only these icons will be shown.
   * Icon names are PascalCase (e.g. "Heart", "Star", "MapPin")
   */
  allowedIcons?: string[];
};

export function FormIconSelect({
  name,
  label,
  hint,
  requiredMark,
  placeholder = "ເລືອກ icon...",
  className,
  allowedIcons,
}: FormIconSelectProps) {
  const { Controller, useFormContext } = RHF;
  const { control } = useFormContext();
  const [open, setOpen] = useState(false);

  return (
    <Field name={name} label={label} hint={hint} requiredMark={requiredMark}>
      <Controller
        control={control}
        name={name as never}
        render={({ field }) => (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={open}
                className={cn(
                  "w-full justify-between",
                  !field.value && "text-muted-foreground",
                  className,
                )}
              >
                <div className="flex items-center gap-2">
                  {field.value ? (
                    <>
                      <DynamicIcon
                        name={field.value}
                        className="h-4 w-4 shrink-0"
                      />
                      <span className="truncate">{field.value}</span>
                    </>
                  ) : (
                    <span>{placeholder}</span>
                  )}
                </div>
                <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-[600px] p-0">
              <CommandIcons
                field={field}
                setOpen={setOpen}
                allowedIcons={allowedIcons}
              />
            </DialogContent>
          </Dialog>
        )}
      />
    </Field>
  );
}

const CommandIcons = ({
  field,
  setOpen,
  allowedIcons,
}: {
  field: { value: string; onChange: (value: string) => void };
  setOpen: (open: boolean) => void;
  allowedIcons?: string[];
}) => {
  const [search, setSearch] = useState("");
  const parentRef = useRef<HTMLDivElement>(null);

  const baseIcons = useMemo(() => {
    if (!allowedIcons || allowedIcons.length === 0) {
      return iconNames;
    }
    const allowedSet = new Set(
      allowedIcons.map((a) => (a.startsWith("Icon") ? a.slice(4) : a)),
    );
    return iconNames.filter((name) => {
      const nameNorm = name.replace(/^Icon/, "");
      return (
        allowedIcons.includes(name) ||
        allowedSet.has(name) ||
        allowedSet.has(nameNorm)
      );
    });
  }, [allowedIcons]);

  const filteredIcons = useMemo(() => {
    if (!search.trim()) return baseIcons;
    const lowerSearch = search.toLowerCase();
    return baseIcons.filter((iconName) =>
      iconName.toLowerCase().includes(lowerSearch),
    );
  }, [search, baseIcons]);

  const columns = 8;
  const rowCount = Math.ceil(filteredIcons.length / columns);

  const rowVirtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 80,
    overscan: 5,
  });

  return (
    <Command shouldFilter={false}>
      <CommandInput
        placeholder="ຄົ້ນຫາ icon..."
        value={search}
        onValueChange={setSearch}
        className="h-9"
      />
      <CommandList className="max-h-[400px] overflow-auto">
        {filteredIcons.length === 0 ? (
          <CommandEmpty>ບໍ່ພົບ icon</CommandEmpty>
        ) : (
          <CommandGroup>
            <div ref={parentRef} className="max-h-[360px] overflow-auto p-3">
              <div
                style={{
                  height: rowVirtualizer.getTotalSize(),
                  width: "100%",
                  position: "relative",
                }}
              >
                {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                  const rowIndex = virtualRow.index;
                  const startIndex = rowIndex * columns;
                  const rowItems = filteredIcons.slice(
                    startIndex,
                    startIndex + columns,
                  );

                  return (
                    <div
                      key={virtualRow.key}
                      data-index={rowIndex}
                      ref={rowVirtualizer.measureElement}
                      className="absolute top-0 left-0 w-full"
                      style={{
                        transform: `translateY(${virtualRow.start}px)`,
                      }}
                    >
                      <div className="mb-2 grid grid-cols-4 gap-2">
                        {rowItems.map((iconName) => (
                          <button
                            key={iconName}
                            type="button"
                            onClick={() => {
                              field.onChange(iconName);
                              setOpen(false);
                              setSearch("");
                            }}
                            className={cn(
                              "relative flex flex-col items-center justify-center gap-1 rounded-md border p-2 text-[9px] leading-tight transition-colors hover:bg-accent hover:text-accent-foreground",
                              field.value === iconName &&
                                "border-primary bg-accent text-accent-foreground",
                            )}
                            title={iconName}
                          >
                            <DynamicIcon
                              name={iconName}
                              className="h-5 w-5 shrink-0"
                            />
                            <span className="max-w-full truncate">
                              {iconName}
                            </span>
                            {field.value === iconName && (
                              <Check className="absolute top-1 right-1 h-3 w-3 text-primary" />
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </CommandGroup>
        )}
      </CommandList>
    </Command>
  );
};
